import _ from 'lodash';
import ActiveSession, { SessionCustomer } from 'models/active_session';
import AutoAcceptedSessionNotification from 'models/notification/auto_accepted_session_notification';
import {
  checkAndResetNavigatingToNext,
  handleNotifiableEvent,
  removeEmptyQueueNotifications,
} from 'actions/conversation/lib/conversation_workflow';
import CustomerView from 'models/location/customer_view';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import { getActiveConversationId } from 'actions/conversation/lib/conversation_helpers';
import { getLatestManualCustomerConversationItemAttrs } from 'scripts/application/lib/conversation_history_helpers';
import { getNotificationType } from 'actions/notification/lib/notification_constants';
import { getOfferedSessionForAgent } from './session_finder';
import { getTextForContent } from 'models/conversation_item';
import IncomingConcurrentSessionNotification from 'models/notification/incoming_concurrent_session_notification';
import isCustomerLoaded from 'actions/customer/lib/is_customer_loaded';
import loadCustomer from 'actions/customer/lib/load_customer';
import navigateToConversation from 'actions/conversation/lib/navigate_to_conversation';
import playNotificationSound from 'actions/notification/lib/play_notification_sound';
import requestSessionDependencies from 'actions/concurrent_messaging/request_session_dependencies';
import RoutingEventType from 'models/routing_event/routing_event_type';
import RoutingQueueItem, {
  AgentParticipant,
  MailSessionTypes,
  MessagingSessionTypes,
  RoutingStatus,
} from 'models/routing_queue_item';
import { sessionTypeForContent } from 'actions/concurrent_messaging/active_session_customer_history_observer';
import ShowConversationSystemNotification from 'actions/notification/show_conversation_system_notification';

export default class SessionRoutingEventObserver {
  constructor(context) {
    this.context = context;
  }

  handleRoutingEvent(eventDto, correlationId) {
    switch (eventDto.type) {
      case RoutingEventType.INCOMING_CONCURRENT_MESSAGE:
        this.handleIncomingConcurrentMessageEvent(eventDto);
        break;
      case RoutingEventType.INCOMING_CONCURRENT_SESSION:
        this.handleIncomingConcurrentSessionEvent(eventDto, correlationId);
        break;
      default:
    }
  }

  handleIncomingConcurrentMessageEvent(eventDto) {
    const sessionId = eventDto.queueItemId;
    let activeSession = this.context.stores.activeSessions.findBy({ id: sessionId });
    const messageText = getTextForContent(eventDto.conversationItem.content);

    let currentAgentId = this.context.stores.currentAgent.get().id;
    if (!activeSession) {
      let conversationId = eventDto.conversationItem.conversationId;
      activeSession = ActiveSession.create({
        id: sessionId,
        messageText,
        queueItem: RoutingQueueItem.create({
          id: sessionId,
          conversationId,
          agents: [AgentParticipant.create({ id: currentAgentId, updatedAt: new Date().toISOString() })],
        }),
        customer: SessionCustomer.create({
          id: eventDto.conversationItem.customerId,
        }),
      });

      checkAndResetNavigatingToNext(this.context);
      requestSessionDependencies(this.context, activeSession.customer.id, sessionId, conversationId);
    } else {
      activeSession.update({ messageText });
    }
    this.context.stores.activeSessions.addOrReplace(activeSession);

    removeEmptyQueueNotifications(this.context);

    let queueItem = activeSession.queueItem;
    let isCurrentAgentOwner = queueItem.getAssignedAgentId() === currentAgentId;
    if (isCurrentAgentOwner && queueItem.status === RoutingStatus.ACTIVE) {
      if (MessagingSessionTypes.indexOf(sessionTypeForContent(eventDto.conversationItem.content)) !== -1) {
        playNotificationSound(getNotificationType(eventDto.type));
      }
      let notification = this.context.stores.notifications.findBy({ id: queueItem.id });
      let isAutoAssignedNotification = notification && notification instanceof AutoAcceptedSessionNotification;
      if (isAutoAssignedNotification) {
        notification.updateMessageText(messageText);
        this.context.stores.notifications.replace(notification);
        return;
      }
      handleNotifiableEvent(this.context, Object.assign({}, eventDto, { profile: activeSession.customer.toJs() }));
    }
  }

  handleIncomingConcurrentSessionEvent(eventDto, correlationId) {
    let queueItem = RoutingQueueItem.fromJs(eventDto.queueItem);
    let customer = SessionCustomer.create({
      id: queueItem.customerId,
    });

    const currentAgentId = this.context.stores.currentAgent.get().id;
    let isNavigatingToNext = false;
    let activeSession = this.context.stores.activeSessions.findBy({ id: queueItem.id });
    if (!activeSession) {
      activeSession = ActiveSession.create({
        id: queueItem.id,
        customer,
        messageText: eventDto.messageText,
        queueItem,
      });
      isNavigatingToNext = checkAndResetNavigatingToNext(this.context);
      requestSessionDependencies(this.context, activeSession.customer.id, queueItem.id, activeSession.conversationId());
    } else {
      const incomingSessionAlreadyAssigned =
        queueItem.status === RoutingStatus.ACTIVE && queueItem.getAssignedAgentId() === currentAgentId;
      const incomingMessageAlreadyProcessed = !!activeSession.messageText;

      activeSession.update({ customer, queueItem });

      // this addresses the race condition where the message event comes in before this session event
      const latestSessionType = activeSession.queueItem.getLatestSession().type;
      if (incomingSessionAlreadyAssigned && incomingMessageAlreadyProcessed) {
        notification = AutoAcceptedSessionNotification.create({
          id: queueItem.id,
          profile: activeSession.customer,
          conversationId: activeSession.conversationId(),
          messageText: activeSession.messageText,
          sessionType: latestSessionType,
        });
        this.context.stores.notifications.addOrReplace(notification);

        if (MessagingSessionTypes.indexOf(latestSessionType) !== -1) {
          playNotificationSound(getNotificationType(eventDto.type));
        }
      }
    }

    const isBeingOfferedToCurrentAgent =
      (queueItem.status === RoutingStatus.OFFERED ||
        queueItem.status === RoutingStatus.OFFERED_REQUEUED ||
        queueItem.status === RoutingStatus.OFFERED_ACTIVE) &&
      queueItem.getOfferingAgentId() === currentAgentId;

    if (isBeingOfferedToCurrentAgent) {
      const alreadyOfferedSession = getOfferedSessionForAgent(this.context.stores.activeSessions, currentAgentId);
      if (alreadyOfferedSession && queueItem.id !== alreadyOfferedSession.queueItem.id) {
        ErrorReporter.reportError(Error('agent offered new concurrent message even though they already have one'), {
          extra: { correlationId, incoming: queueItem, existing: alreadyOfferedSession.queueItem },
        });
      }
    }

    removeEmptyQueueNotifications(this.context);

    if (isCustomerLoaded(this.context, activeSession.customer.id)) {
      let customerStores = this.context.stores.customers.storesFor(activeSession.customer.id);
      let customerName = customerStores.profile.get().name;
      if (customerName) {
        activeSession.updateCustomerName(customerName);
      }

      let latestCustomerItem = getLatestManualCustomerConversationItemAttrs({
        conversationHistory: customerStores.conversationHistory,
        conversationId: getActiveConversationId(customerStores.conversations),
        fields: ['content'],
      });
      if (latestCustomerItem) {
        let messageText = getTextForContent(latestCustomerItem.content);
        if (messageText) {
          activeSession.update({ messageText });
        }
      }
    } else {
      loadCustomer(this.context, activeSession.customer.id);
    }

    const latestSessionType = activeSession.queueItem.getLatestSession().type;
    let notification = IncomingConcurrentSessionNotification.create({
      id: queueItem.id,
      profile: activeSession.customer,
      sessionType: latestSessionType,
      type: eventDto.type,
    });

    if (this.shouldAutoAcceptSession(queueItem, currentAgentId)) {
      this.context.capacityManager.postponeMessagingRouting();
      activeSession.accept(currentAgentId);

      this.context.gateways.routingEvent.publish(currentAgentId, {
        queueItemId: activeSession.id,
        type: RoutingEventType.ACCEPTED_OFFERED_CONCURRENT_SESSION,
      });

      if (MailSessionTypes.indexOf(latestSessionType) > -1) {
        if (this._isViewingCustomer(activeSession.customer.id)) {
          this.context.stores.activeSessions.addOrReplace(activeSession);
          return;
        }

        if (isNavigatingToNext) {
          navigateToConversation(this.context, {
            customerId: activeSession.customer.id,
            conversationId: activeSession.conversationId(),
          });
          this.context.stores.activeSessions.addOrReplace(activeSession);
          return;
        }
      }

      notification = AutoAcceptedSessionNotification.create({
        id: queueItem.id,
        profile: activeSession.customer,
        conversationId: activeSession.conversationId(),
        messageText: activeSession.messageText,
        sessionType: activeSession.queueItem.getLatestSession().type,
      });
      this.context.stores.notifications.addOrReplace(notification);
    }

    this.context.stores.activeSessions.addOrReplace(activeSession);

    if (!isBeingOfferedToCurrentAgent) {
      return;
    }

    if (MessagingSessionTypes.indexOf(queueItem.getLatestSession().type) !== -1) {
      playNotificationSound(getNotificationType(eventDto.type));
    }

    this.context.executeAction(ShowConversationSystemNotification, notification);
  }

  shouldAutoAcceptSession(queueItem, currentAgentId) {
    let messagingConfiguration = this.context.stores.messagingConfiguration.get();
    let autoAssignMessagingSessions = _.get(messagingConfiguration, 'autoAssignMessagingSessions');
    return (
      (autoAssignMessagingSessions || MailSessionTypes.indexOf(queueItem.getLatestSession().type) > -1) &&
      queueItem.getOfferingAgentId() === currentAgentId
    );
  }

  _isViewingCustomer(customerId) {
    let currentLocation = this.context.stores.currentLocation.get();
    return currentLocation instanceof CustomerView && currentLocation.customerId === customerId;
  }
}



// WEBPACK FOOTER //
// ./src/scripts/application/actions/concurrent_messaging/active_session_routing_event_observer.js